import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";
import { Box, Divider, Tab, Tabs} from "@mui/material";
import { FC } from "react";
import { Helmet } from 'react-helmet-async'
import { Link, matchPath, Redirect, useLocation } from "react-router-dom";
import { Content } from "@/components/content/Content";
import { OdResourcePageProps } from "@/types/resource";
import { useResourceRegister } from "@/utils/resourceRegister";
import withCompositeResource from "@/components/recource/CompositeResource";
import { useHasFeature } from "@/utils/useHasFeature";
import { KEY_MANAGEMENT, MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES, MANAGE_SECUREFILE_SETTINGS } from "@/constants";
import { getSettingsMenuDefaultRoute } from "@/authProvider/permissionMap";
import { useOdPermissions } from "@/authProvider/useOdPermissions";


const basicResource = "settings-clients-securefile-config"
const customKeyResource = 'settings-clients-securefile-customkey-config'
const clientsSecurefileRes = 'settings-clients-securefile'
const securefilePerClientSettingRes = 'settings-securefile-per-client'
const autoDiscoveryResource = "settings-clients-securefile-autodiscovery"
const legalWarningResource = 'settings-clients-securefile-legal-warning-config'

const SecureFileSettings: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: basicResource })
  useResourceRegister({ name: customKeyResource })
  useResourceRegister({ name: clientsSecurefileRes })
  useResourceRegister({ name: securefilePerClientSettingRes })
  useResourceRegister({ name: autoDiscoveryResource })
  useResourceRegister({ name: legalWarningResource })

  const ready = useSelector<ReduxState, boolean>(store => !!store.admin.resources[basicResource])
  return (
    ready ? <SecureFileSettingsBody {...props} /> : null
  )
}

type TabOption = 'basic' | 'customkeyvault' | 'auto-discovery' | 'legal-warning' | 'classification-n-protection'

const tabSubRoutes = ['basic', 'customkeyvault', 'auto-discovery', 'legal-warning', 'classification-n-protection']

const SecureFileSettingsBody: FC<OdResourcePageProps> = ({ 
  children, 
  options,
}) => {
  const location = useLocation()
  const { rest } = 
    matchPath<{rest:string}>(location.pathname, { path: '/settings/securefile/:rest'})?.params || {}
  const permissions = useOdPermissions()
  const hasRulesPermit = permissions.has(MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES)
  const hasSettingsPermit = permissions.has(MANAGE_SECUREFILE_SETTINGS)
  const hasKeyManagePermit = permissions.has(KEY_MANAGEMENT)
  const hasFeature = useHasFeature()
  const activeItem = rest ?? getSettingsMenuDefaultRoute("SecureFileSettings", permissions)
  const isTabSubRoute = !activeItem || tabSubRoutes.indexOf(activeItem) > -1
  
  if (!hasFeature.OnDataSecureFile || (!hasSettingsPermit && !hasRulesPermit)
    || (isTabSubRoute && (
      (!hasSettingsPermit && activeItem !== 'classification-n-protection')
      || (!hasRulesPermit && activeItem === 'classification-n-protection')
    ))
  ) {
    return <Redirect to="/" />
  }

  const tabs = [
    ...(hasSettingsPermit ? [<Tab label="General" value="basic" key={1} component={Link} to="/settings/securefile/basic" />] : []),
    ...(hasRulesPermit ? [<Tab label="Classification and Protection" key={2} value="classification-n-protection" component={Link} to="/settings/securefile/classification-n-protection" />] : []),
    ...(hasSettingsPermit ? [
      <Tab label="Auto Discovery" value="auto-discovery" key={3} component={Link} to="/settings/securefile/auto-discovery" />,
      <Tab label="Decryption Legal Warning" value="legal-warning" key={4} component={Link} to="/settings/securefile/legal-warning" />,
      ...(hasKeyManagePermit ? [<Tab label="Custom Key Vault" value="customkeyvault" key={5} component={Link} to="/settings/securefile/customkeyvault" />] : []),
    ] : [])
  ]

  // [2024-10-17]
  // one side note about the "Custom Key Vault" permission control:
  // for now, user should have MANAGE_SECUREFILE_SETTINGS permission to see the custom key vault
  // settings page, but when user updates the settings, the API enforces an additional permit check 
  // over KEY_MANAGEMENT permission.
  // it's a similar case for "Onpremises Data Analyzer" settings too, user needs MANAGE_SECUREFILE_SETTINGS permission 
  // to see the ui, but update actions requires MANAGE_ONDATA_CLIENTS permission
  return (
    <>
      <Helmet>
        <title>SecureFile Settings | OnData</title>
      </Helmet>
      {isTabSubRoute
        ? <Content pageName="SecureFile Settings" resourceName="SecureFile Settings" breadcrumbs={options?.breadcrumbs}>
            <Tabs
              indicatorColor="secondary"
              scrollButtons="auto"
              textColor="primary"
              variant="scrollable"
              value={activeItem ?? 'basic'}
            >
              {tabs}
            </Tabs>
            <Divider />
            <Box sx={{ mt: 3 }}>
              {children}
            </Box>
          </Content>
        : children}
    </>
  )
}

export const SecureFileSettingsComposite = withCompositeResource(SecureFileSettings);


